import { Injectable } from '@angular/core';
import { TimeService } from '@services/common/time.service';
import { SchedulerCalendarComponent } from '@shared/components/scheduler-calendar/scheduler-calendar.component';
import { isWeekend } from 'date-fns';

import { legacyParse } from '@date-fns/upgrade/v2';

@Injectable({
  providedIn: 'root'
})
export class GenerateMonthService {
  monthList: any = [];
  lastWeekDay = 7;
  dayOfMonth = 1;
  firstDayOfMonth: number;

  days: any = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  getLastDayOfPreviousMonth(year: number, month: number) {
    const lastDayPreviousMonth = new Date(year, month, 1);
    lastDayPreviousMonth.setDate(1);
    lastDayPreviousMonth.setHours(-1);

    return lastDayPreviousMonth.getDate();
  }

  generateMonths(calendarParams, component: SchedulerCalendarComponent): Array<any> {
    const { weeksInMonth, month, year } = calendarParams;
    this.monthList = [];
    this.dayOfMonth = 1;
    this.firstDayOfMonth = new Date(year, month - 1, 1).getDay() - 1;
    const today = this._timeService.getDateFormat(new Date(), 'YYYY-MM-DD');
    const lastDayPreviousMonth = this.getLastDayOfPreviousMonth(year, month - 1);
    const monthParams = { ...calendarParams, year, month, lastDayPreviousMonth, component, today };

    if (this.firstDayOfMonth === -1) {
      this.firstDayOfMonth = 6;
    }

    this.handleMonth(weeksInMonth, monthParams);

    return this.monthList;
  }

  handleMonth(weeksInMonth: number, monthParams): void {
    for (let weekIndex = 0; weekIndex < weeksInMonth; weekIndex++) {
      const weekParams = { ...monthParams, weeksInMonth };
      this.monthList[weekIndex] = [];

      if (weekIndex > 0) {
        this.firstDayOfMonth = 0;
      }

      this.handleWeek(weekIndex, weekParams);
    }
  }

  handleWeek(weekIndex: number, weekParams) {
    for (let dayInWeek = 0; dayInWeek < this.lastWeekDay; dayInWeek++) {
      const dayParams = { ...weekParams, dayInWeek, weekIndex };
      if (dayInWeek >= this.firstDayOfMonth) {
        // handle day in current month
        this.handleCurrentMonth(dayParams);
      } else if (dayInWeek < this.firstDayOfMonth) {
        // handle day from end of previous month
        this.handleEndOfPreviousMonth(dayParams);
      } else {
        // handle day from start of next month
        this.handleStartOfNextMonth(dayParams);
      }
    }
  }

  handleCurrentMonth(params): void {
    const { year, month, dayInWeek, component, weekIndex, weeksInMonth, today } = params;
    const currentDate = new Date(year, month - 1, this.dayOfMonth);
    const dateIndex = currentDate.getDate();
    const dayDateStr = currentDate.toDateString();
    const formattedDay = this._timeService.getDateFormat(dayDateStr, 'YYYY-MM-DD');

    this.monthList[weekIndex][dayInWeek] = {
      isWeekend: isWeekend(legacyParse(currentDate)),
      day: dateIndex,
      date: formattedDay,
      isNotCurrentMonth: weeksInMonth - 1 === weekIndex && dateIndex < 10,
      isToday: formattedDay === today
    };

    if (weekIndex === 0) {
      this.monthList[weekIndex][dayInWeek].dayName = this.days[dayInWeek];
    }

    component.daysObject[formattedDay] = {
      progresses: []
    };

    component.daysObject2[formattedDay] = {
      progresses: []
    };

    this.dayOfMonth++;
  }

  handleEndOfPreviousMonth(params): void {
    const { year, month, lastDayPreviousMonth, dayInWeek, component, weekIndex } = params;
    const currentDate = new Date(year, month - 2, lastDayPreviousMonth - (this.firstDayOfMonth - dayInWeek - 1));
    const dateNumber = currentDate.getDate();
    const formattedDay = this._timeService.getDateFormat(currentDate, 'YYYY-MM-DD');

    component.daysObject[formattedDay] = {
      progresses: []
    };

    component.daysObject2[formattedDay] = {
      progresses: []
    };

    this.monthList[weekIndex][dayInWeek] = {
      isWeekend: isWeekend(legacyParse(currentDate)),
      day: dateNumber,
      date: formattedDay,
      dayName: this.days[dayInWeek],
      isNotCurrentMonth: true
    };
  }

  private handleStartOfNextMonth(params): void {
    const { weekIndex, dayInWeek } = params;
    this.monthList[weekIndex][dayInWeek] = {
      dayName: this.days[dayInWeek],
      isNotCurrentMonth: ''
    };
  }

  constructor(private _timeService: TimeService) {}
}
